import type { RelatorioDTO } from "@caint/shared";
import { useCallback, useEffect, useState } from "react";
import { ApiError, apiClient } from "../../lib/api.ts";
import { RelatorioIndisponivel } from "./RelatorioIndisponivel.tsx";
import { RelatorioInstitucional } from "./RelatorioInstitucional.tsx";
import "../../styles/relatorio-institucional.css";

type Estado = "carregando" | "pronto" | "indisponivel" | "erro";

/**
 * Tela interna "Relatório da Unidade" (avaliadores/gestão/AUDIN): busca o
 * Relatório Preliminar da avaliação atual e o exibe INLINE, com botão de emissão
 * em PDF. Quando ainda não validado (409), mostra a mensagem amigável de
 * indisponibilidade em vez de um erro cru. Usada por desktop e mobile.
 */
export function RelatorioUnidadeInterno({
  avaliacaoId,
  token,
}: {
  avaliacaoId: string | null;
  token: string | null;
}) {
  const [dto, setDto] = useState<RelatorioDTO | null>(null);
  const [estado, setEstado] = useState<Estado>("carregando");
  const [msgErro, setMsgErro] = useState<string | null>(null);

  const carregar = useCallback(async () => {
    if (!avaliacaoId || !token) {
      setEstado("indisponivel");
      return;
    }
    setEstado("carregando");
    try {
      setDto(await apiClient.relatorioDaAvaliacao(avaliacaoId, token));
      setEstado("pronto");
    } catch (e) {
      if (e instanceof ApiError && e.status === 409) {
        setEstado("indisponivel");
        return;
      }
      setMsgErro(e instanceof ApiError ? e.message : "Falha ao carregar o relatório.");
      setEstado("erro");
    }
  }, [avaliacaoId, token]);

  useEffect(() => {
    void carregar();
  }, [carregar]);

  return (
    <div className="ri-tela">
      <div className="ri-tela-topo no-print">
        <div>
          <div className="page-eyebrow">Relatórios</div>
          <h2 className="ri-tela-titulo">Relatório da Unidade</h2>
        </div>
        {estado === "pronto" ? (
          <button type="button" className="m-btn primary" onClick={() => window.print()}>
            🖨 Emitir / PDF
          </button>
        ) : null}
      </div>

      {estado === "carregando" ? <p className="ri-tela-msg">Carregando…</p> : null}
      {estado === "indisponivel" ? <RelatorioIndisponivel contexto="interno" /> : null}
      {estado === "erro" ? <p className="ri-tela-msg ri-tela-erro">{msgErro}</p> : null}
      {estado === "pronto" && dto ? <RelatorioInstitucional dto={dto} /> : null}
    </div>
  );
}
